<template>
  <div class="searchable-dropdown">
    <input
      :id="inputId"
      type="text"
      v-model="searchText"
      @input="filterItems"
      @focus="showDropdown = true"
      :placeholder="placeholder"
      autocomplete="off"
      class="search-input"
    />
    <div v-if="showDropdown && filteredItems.length > 0" class="dropdown-results">
      <div
        v-for="item in filteredItems"
        :key="item[itemKey]"
        @click="selectItem(item)"
        class="dropdown-item"
        :class="{ selected: value === item[itemKey] }"
      >
        {{ item[itemLabel] }}
      </div>
    </div>
    <div v-if="showDropdown && searchText && filteredItems.length === 0" class="dropdown-results">
      <div class="dropdown-item no-results">No results found</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchableDropdown',
  props: {
    value: {
      type: [Number, String],
      default: null
    },
    items: {
      type: Array,
      required: true
    },
    itemKey: {
      type: String,
      default: 'id'
    },
    itemLabel: {
      type: String,
      default: 'name'
    },
    placeholder: {
      type: String,
      default: 'Search...'
    },
    inputId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      searchText: '',
      filteredItems: [],
      showDropdown: false
    };
  },
  watch: {
    items: {
      immediate: true,
      handler(newItems) {
        this.filteredItems = [...newItems].sort((a, b) => b[this.itemKey] - a[this.itemKey]);
      }
    },
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          const selectedItem = this.items.find(item => item[this.itemKey] === newValue);
          if (selectedItem) {
            this.searchText = selectedItem[this.itemLabel];
          }
        } else {
          this.searchText = '';
        }
      }
    }
  },
  methods: {
    fuzzyMatch(searchText, targetText) {
      const search = searchText.toLowerCase().trim();
      const target = targetText.toLowerCase();

      if (!search) return true;

      // Split search into terms, escape special regex chars, and create pattern
      // Pattern matches each term at word boundaries (start of words)
      const searchTerms = search.split(/\s+/).map(term => {
        // Escape special regex characters
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Match at word boundary (after non-alphanumeric or start of string)
        return `(?:^|[^a-z0-9])${escaped}`;
      });

      // All terms must match
      const pattern = searchTerms.map(term => `(?=.*${term})`).join('');
      const regex = new RegExp(pattern);

      return regex.test(target);
    },
    filterItems() {
      if (!this.searchText) {
        this.filteredItems = [...this.items].sort((a, b) => b[this.itemKey] - a[this.itemKey]);
        return;
      }

      this.filteredItems = this.items
        .filter(item => {
          const searchableText = item[this.itemLabel];
          return this.fuzzyMatch(this.searchText, searchableText);
        })
        .sort((a, b) => b[this.itemKey] - a[this.itemKey]);

      this.showDropdown = true;
    },
    selectItem(item) {
      this.$emit('input', item[this.itemKey]);
      this.$emit('change', item);
      this.searchText = item[this.itemLabel];
      this.showDropdown = false;
    },
    handleClickOutside(event) {
      const input = this.$el.querySelector('.search-input');
      if (input && !input.contains(event.target) && !event.target.closest('.dropdown-results')) {
        this.showDropdown = false;
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
.searchable-dropdown {
  position: relative;
  width: 100%;
}

.search-input {
  width: 100%;
  box-sizing: border-box;
}

.dropdown-results {
  position: absolute;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  margin-top: 2px;
  min-width: 300px;
  width: 100%;
}

.dropdown-item {
  padding: 10px 15px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}

.dropdown-item.selected {
  background-color: #e6f7ff;
  font-weight: bold;
}

.dropdown-item.no-results {
  cursor: default;
  color: #999;
  font-style: italic;
}

.dropdown-item.no-results:hover {
  background-color: white;
}
</style>
